import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Box,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Pagination,
  TextField,
  InputAdornment,
  Switch,
  FormControlLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import {
  getAllPaginatedCampaigns,
  deleteCampaign,
  toggleCampaignArchive,
  archiveCampaign,
  unarchiveCampaign,
} from "../../../api/Campaigns";

function Campaigns() {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [openArchiveDialog, setOpenArchiveDialog] = useState(false);
  const [campaignToArchive, setCampaignToArchive] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filterParam, setFilterParam] = useState("animal");
  const [showArchived, setShowArchived] = useState(false);

  // Map tab index to campaign type for filtering
  const tabToFilterMap = [
    "animal", // Animal campaigns
    "product", // Product campaigns
    "capital", // Capital campaigns
  ];

  useEffect(() => {
    fetchCampaigns();
  }, [page, tabValue, searchParam, showArchived]);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      // Set the filter parameter based on the current tab
      const currentFilter = tabToFilterMap[tabValue];
      setFilterParam(currentFilter);

      // Call the API with the current page, search, filter parameters, and show_archived
      const response = await getAllPaginatedCampaigns(
        page,
        searchParam,
        "",
        currentFilter,
        showArchived
      );

      if (response && response.results) {
        setCampaigns(response.results);

        // Calculate total pages based on count and page size
        const count = response.count;
        const pageSize = 6; // Same as defined in the backend
        setTotalPages(Math.ceil(count / pageSize));
      } else {
        setCampaigns([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Error fetching campaigns:", err);
      setError("Failed to fetch campaigns");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(1); // Reset to first page when changing tabs
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
    // If the search input is cleared, trigger a search immediately
    if (event.target.value === "" && searchParam !== "") {
      setSearchParam("");
      setPage(1); // Reset to first page when clearing search
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearchParam(searchInput);
    setPage(1); // Reset to first page when searching
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchParam("");
    setPage(1);
  };

  const handleDeleteClick = (campaign) => {
    setCampaignToDelete(campaign);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      // Call the API to delete the campaign
      await deleteCampaign(campaignToDelete.id, campaignToDelete.campaign_type);

      // Refresh the campaigns list
      fetchCampaigns();

      setOpenDeleteDialog(false);
      setCampaignToDelete(null);
    } catch (err) {
      console.error("Error deleting campaign:", err);
      setError("Failed to delete campaign");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setCampaignToDelete(null);
  };

  const handleArchiveClick = (campaign) => {
    setCampaignToArchive(campaign);
    setOpenArchiveDialog(true);
  };

  const handleArchiveCancel = () => {
    setOpenArchiveDialog(false);
    setCampaignToArchive(null);
  };

  const handleArchiveConfirm = async () => {
    try {
      setLoading(true);

      // Call the API to toggle archive status
      if (campaignToArchive.archived) {
        // If campaign is already archived, unarchive it
        await unarchiveCampaign(
          campaignToArchive.id,
          campaignToArchive.campaign_type
        );
      } else {
        // If campaign is not archived, archive it
        await archiveCampaign(
          campaignToArchive.id,
          campaignToArchive.campaign_type
        );
      }

      // Refresh the campaigns list
      fetchCampaigns();

      // Clear the state
      setOpenArchiveDialog(false);
      setCampaignToArchive(null);
    } catch (err) {
      console.error("Error toggling archive status:", err);
      setError("Failed to update archive status");
    } finally {
      setLoading(false);
    }
  };

  const handleShowArchivedToggle = () => {
    setShowArchived(!showArchived);
    setPage(1); // Reset to first page when toggling archived view
  };

  const handleCampaignClick = (campaign) => {
    // Navigate to the appropriate campaign page based on campaign type
    if (campaign.campaign_type === "animal") {
      navigate(
        `/campaigns/animal/${campaign.nonprofit_name || "nonprofit"}/${
          campaign.animal_name || "animal"
        }/${campaign.id}`
      );
    } else {
      navigate(
        `/campaigns/${campaign.campaign_type}/${
          campaign.nonprofit_name || "nonprofit"
        }/${campaign.campaign_title.replace(/\s+/g, "-").toLowerCase()}/${
          campaign.id
        }`
      );
    }
  };

  const renderCampaignsTable = () => {
    return (
      <TableContainer
        component={Paper}
        sx={{ maxWidth: "100%", overflowX: "auto" }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Campaign Title</TableCell>
              <TableCell>Campaign Type</TableCell>
              <TableCell>Donation Amount</TableCell>
              <TableCell>Donation Goal</TableCell>
              <TableCell align="center">Archived</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.length > 0 ? (
              campaigns.map((campaign) => (
                <TableRow
                  key={campaign.id}
                  sx={{
                    backgroundColor: campaign.archived
                      ? "rgba(0, 0, 0, 0.04)"
                      : "inherit",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.08)",
                    },
                  }}
                  onClick={(e) => {
                    // Prevent row click when clicking on action buttons
                    if (e.target.closest("button") === null) {
                      handleCampaignClick(campaign);
                    }
                  }}
                >
                  <TableCell>{campaign.id}</TableCell>
                  <TableCell>{campaign.campaign_title}</TableCell>
                  <TableCell>{campaign.campaign_type}</TableCell>
                  <TableCell>
                    ${parseFloat(campaign.donation_amount).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    ${parseFloat(campaign.donation_goal).toFixed(2)}
                  </TableCell>
                  <TableCell align="center">
                    {typeof campaign.archived === "boolean"
                      ? campaign.archived
                        ? "Yes"
                        : "No"
                      : "Unknown"}
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{ display: "flex", gap: 1, justifyContent: "center" }}
                    >
                      <Button
                        variant="contained"
                        color={campaign.archived ? "secondary" : "primary"}
                        startIcon={
                          campaign.archived ? (
                            <UnarchiveIcon />
                          ) : (
                            <ArchiveIcon />
                          )
                        }
                        onClick={() => handleArchiveClick(campaign)}
                        size="small"
                        data-archive-status={
                          campaign.archived ? "archived" : "not-archived"
                        }
                      >
                        {campaign.archived ? "Unarchive" : "Archive"}
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteClick(campaign)}
                        size="small"
                      >
                        Delete
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No campaigns found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading && campaigns.length === 0) {
    return (
      <Container>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Campaign Management
      </Typography>

      {error && (
        <Box mb={2}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {/* Search Bar */}
      <Box mb={3}>
        <form onSubmit={handleSearchSubmit}>
          <TextField
            fullWidth
            placeholder="Search by ID or Campaign Title"
            value={searchInput}
            onChange={handleSearchChange}
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <Button onClick={handleClearSearch} size="small">
                    Clear
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </form>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="campaign tabs"
          >
            <Tab label="Animal Campaigns" />
            <Tab label="Product Campaigns" />
            <Tab label="Capital Campaigns" />
          </Tabs>
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={showArchived}
              onChange={handleShowArchivedToggle}
              color="primary"
            />
          }
          label="Show Archived"
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {renderCampaignsTable()}

          {/* Pagination */}
          <Box display="flex" justifyContent="center" mt={4}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirm Campaign Deletion"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete the campaign "
            {campaignToDelete?.campaign_title}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog
        open={openArchiveDialog}
        onClose={handleArchiveCancel}
        aria-labelledby="archive-dialog-title"
        aria-describedby="archive-dialog-description"
      >
        <DialogTitle id="archive-dialog-title">
          {campaignToArchive?.archived
            ? "Confirm Campaign Unarchive"
            : "Confirm Campaign Archive"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="archive-dialog-description">
            {campaignToArchive?.archived
              ? `Are you sure you want to unarchive the campaign "${campaignToArchive?.campaign_title}"? This will make the campaign visible to users again.`
              : `Are you sure you want to archive the campaign "${campaignToArchive?.campaign_title}"? Archived campaigns will be hidden from users unless they specifically choose to view archived content.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleArchiveCancel}>Cancel</Button>
          <Button
            onClick={handleArchiveConfirm}
            color={campaignToArchive?.archived ? "secondary" : "primary"}
            autoFocus
          >
            {campaignToArchive?.archived ? "Unarchive" : "Archive"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Campaigns;
